import { useEffect, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Route } from '@metro/routing'
import type { LineDisplay } from '@metro/route-card'
import { MobileRouteDetailsOverlay } from '../../components/MobileRouteDetailsOverlay'
import type { StationResolver, ConvenientCarType } from '@metro/route-details'
import styles from './RouteDetailsVisualFixture.module.css'

/**
 * Private QA fixture for Playwright phone-modal snapshots (RouteDetails
 * P1 spec §10). Mounts a single state of the RouteDetails modal inside
 * a fixed iPhone-16-Pro frame (402 × 874) on a blank backdrop, so each
 * screenshot is pixel-stable and diffs cleanly against the Figma S1–S5
 * baselines.
 *
 * URL contract:
 *   /__visual/routedetails?state=<S1|S2|S3|S4|S5>&theme=<light|dark>
 *
 * `theme` defaults to `light`; `dark` forces `<html data-theme="dark">`.
 * Dev/QA-only route, excluded from indexing via `public/robots.txt`
 * `Disallow: /__visual/`.
 *
 * Fixture data (St Paul's → Tottenham Court Road [→ Bond Street]) mirrors
 * the QA spec §1.3 frames. The Central line block carries 4 middle stops
 * → the expand chip reads "4 stations" (the V1 stop-count formula; this
 * matches the S1 Figma label — S2 reveals 4 middle rows, vs the Figma
 * frame's 3-row shorthand, which is a Figma fixture artifact, not a
 * contract divergence).
 */

type FixtureState = 'S1' | 'S2' | 'S3' | 'S4' | 'S5' | 'WALK'

const VALID_STATES: ReadonlySet<FixtureState> = new Set<FixtureState>([
  'S1',
  'S2',
  'S3',
  'S4',
  'S5',
  'WALK',
])

const LINE_LOOKUP: ReadonlyMap<string, LineDisplay> = new Map([
  ['central', { localName: 'Central line', hexColor: '#ED1C24' }],
  ['elizabeth', { localName: 'Elizabeth line', hexColor: '#702C91' }],
])

const NAMES: Record<string, string> = {
  'st-pauls': "St Paul's",
  chancery: 'Oxford Circus',
  holborn: 'Oxford Circus',
  bank: 'Oxford Circus',
  liverpool: 'Oxford Circus',
  tcr: 'Tottenham Court Road',
  'tcr-e': 'Tottenham Court Road',
  farringdon: 'Oxford Circus',
  whitechapel: 'Oxford Circus',
  canary: 'Oxford Circus',
  custom: 'Oxford Circus',
  bond: 'Bond Street',
  green: 'Green Park',
  baker: 'Baker Street',
}

// Coordinates for the DIRECT-WALK fixture endpoints — feed the walk-route
// map (origin + destination). Under the harness build the map draws the
// deterministic straight-line placeholder (no MapKit), so these only need
// to be non-`(0,0)` to keep the walk-route branch active.
const COORDS: Record<string, { latitude: number; longitude: number }> = {
  green: { latitude: 51.5067, longitude: -0.1428 },
  baker: { latitude: 51.5226, longitude: -0.1571 },
}

const resolveStation: StationResolver = (id) => {
  const localName = NAMES[id]
  if (localName === undefined) return null
  const coordinate = COORDS[id]
  return coordinate !== undefined ? { localName, coordinate } : { localName }
}

// Fixed clock so arrival HH:mm renders byte-identically.
const FIXED_CLOCK = (): Date => new Date('2026-05-25T12:13:00Z')

function ride(
  lineId: string,
  durationS: number,
  stopIds: readonly string[],
): Route['legs'][number] {
  return {
    type: 'ride',
    line_id: lineId,
    duration_s: durationS,
    // Arrival offsets chosen so the first stop reads ~12:30 and the last
    // ~22:46 in the fixed-clock 24h Europe/London format used by iOS.
    stops: stopIds.map((stationId, i) => ({ stationId, arrival_offset_s: 1020 + i * 120 })),
  }
}

// Central block: St Paul's → 4 middles → Tottenham Court Road.
const CENTRAL_BLOCK = ride('central', 1020, [
  'st-pauls',
  'chancery',
  'holborn',
  'bank',
  'liverpool',
  'tcr',
])
// Elizabeth block: Tottenham Court Road → 4 middles → Bond Street.
const ELIZABETH_BLOCK = ride('elizabeth', 1020, [
  'tcr-e',
  'farringdon',
  'whitechapel',
  'canary',
  'custom',
  'bond',
])

const SINGLE_ROUTE: Route = {
  id: 'r-single',
  total_time_s: 1020,
  transfer_count: 0,
  walking_time_s: 0,
  legs: [CENTRAL_BLOCK],
}

const TRANSFER_ROUTE: Route = {
  id: 'r-transfer',
  total_time_s: 2520,
  transfer_count: 1,
  walking_time_s: 0,
  legs: [CENTRAL_BLOCK, { type: 'transfer', duration_s: 480 }, ELIZABETH_BLOCK],
}

// DIRECT WALK route (zero rides): Green Park → Baker Street on foot. The
// tube framing is dropped; rows are [origin marker, street walk, map] and
// the map draws a walking route (deterministic straight line here).
function streetWalkLeg(fromId: string, toId: string, durationS: number): Route['legs'][number] {
  return {
    type: 'walk',
    transition_id: '',
    from_station_id: fromId,
    to_station_id: toId,
    duration_s: durationS,
  }
}

const WALK_ROUTE: Route = {
  id: 'r-walk',
  total_time_s: 600,
  transfer_count: 0,
  walking_time_s: 600,
  legs: [streetWalkLeg('green', 'baker', 600)],
}

// Three chips at the top (Figma frames show 3 route variants).
const CHIP_ROUTES: readonly Route[] = [
  TRANSFER_ROUTE,
  { ...TRANSFER_ROUTE, id: 'r-alt-1', total_time_s: 2600 },
  { ...TRANSFER_ROUTE, id: 'r-alt-2', total_time_s: 2680 },
]

interface StateModel {
  routes: readonly Route[]
  selected: Route
  isPremium: boolean
  possibleTransfersByLeg?: Readonly<Record<number, readonly ConvenientCarType[]>>
  platformBoundsByLeg?: Readonly<Record<number, string>>
}

// Signed tube leg next to an unsigned Elizabeth leg (TfL publishes no
// Elizabeth signs) — S3–S5 show both states. Leg 0 = the Central ride.
const TRANSFER_BOUNDS: Readonly<Record<number, string>> = { 0: 'Westbound' }

function modelFor(state: FixtureState): StateModel {
  switch (state) {
    case 'S1':
    case 'S2':
      return {
        routes: [SINGLE_ROUTE, { ...SINGLE_ROUTE, id: 'r-single-2' }],
        selected: SINGLE_ROUTE,
        isPremium: true,
      }
    case 'S3':
      return {
        routes: CHIP_ROUTES,
        selected: TRANSFER_ROUTE,
        isPremium: true,
        platformBoundsByLeg: TRANSFER_BOUNDS,
      }
    case 'S4':
      return {
        routes: CHIP_ROUTES,
        selected: TRANSFER_ROUTE,
        isPremium: true,
        possibleTransfersByLeg: { 0: ['nearFirst', 'nearEnd'] },
        platformBoundsByLeg: TRANSFER_BOUNDS,
      }
    case 'S5':
      return {
        routes: CHIP_ROUTES,
        selected: TRANSFER_ROUTE,
        isPremium: false,
        possibleTransfersByLeg: { 0: ['nearFirst', 'nearEnd'] },
        platformBoundsByLeg: TRANSFER_BOUNDS,
      }
    case 'WALK':
      return { routes: [WALK_ROUTE], selected: WALK_ROUTE, isPremium: true }
  }
}

function parseState(raw: string | null): FixtureState {
  if (raw !== null && (VALID_STATES as Set<string>).has(raw)) return raw as FixtureState
  return 'S1'
}

export function RouteDetailsVisualFixture(): ReactElement {
  const [params] = useSearchParams()
  const state = parseState(params.get('state'))
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light'
  // `?layout=sidePanel` (TASK-412) renders the tablet/desktop side-panel
  // composition: the content-only overlay inside the `.panel` Liquid-Glass
  // host card. Default `overlay` is the phone modal in the 402×874 frame.
  const layout = params.get('layout') === 'sidePanel' ? 'sidePanel' : 'overlay'
  const model = modelFor(state)

  useEffect(() => {
    const html = document.documentElement
    const prev = html.dataset.theme
    html.dataset.theme = theme
    html.classList.add('visual-fixture-active')
    return () => {
      if (prev === undefined) delete html.dataset.theme
      else html.dataset.theme = prev
      html.classList.remove('visual-fixture-active')
    }
  }, [theme])

  const overlay = (
    <MobileRouteDetailsOverlay
      rawRoutes={model.routes}
      selectedRoute={model.selected}
      isPremium={model.isPremium}
      lineLookup={LINE_LOOKUP}
      resolveStation={resolveStation}
      possibleTransfersByLeg={model.possibleTransfersByLeg}
      platformBoundsByLeg={model.platformBoundsByLeg}
      layout={layout}
      // S2 starts expanded — the fixture drives the toggle on mount via
      // the data-state hook below; S1 and the rest stay collapsed.
      onClose={() => {}}
      onSelectRoute={() => {}}
      onOpenTransferPaywall={() => {}}
      onReportIssue={() => {}}
      clock={FIXED_CLOCK}
      key={state}
    />
  )

  return (
    <div
      className={layout === 'sidePanel' ? styles.frame : styles.phone}
      data-testid="routedetails-visual-fixture"
      data-state={state}
      data-layout={layout}
    >
      {layout === 'sidePanel' ? <div className={styles.panel}>{overlay}</div> : overlay}
      {state === 'S2' && <ExpandFirstBlockOnMount />}
    </div>
  )
}

/**
 * S2 = S1 expanded. The overlay owns expand state internally, so the
 * fixture clicks the first expand button once on mount to reveal the
 * middle stops (deterministic — the button is present in S1's collapsed
 * render).
 */
function ExpandFirstBlockOnMount(): null {
  useEffect(() => {
    const btn = document.querySelector<HTMLButtonElement>(
      '[data-testid="route-details-expand-button-section-0"]',
    )
    btn?.click()
  }, [])
  return null
}

export default RouteDetailsVisualFixture
